/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Input } from 'antd';
import GenericSearchBar from '../../../../../../components/genericSearchBar/GenericSearchBar';
import useDebounce from '../../../../../../hooks/useDebounce';
import { createNewProvider, getProviders } from '../../../../../../services/orders';
import { IproviderInfo, IproductItemData, ISerialErrors } from '../../../../../../types/orders';
import errorMessage from '../../../../../../utils/errorMessage';
import {
  BordOneToneIcon,
  Loading,
  NudosSelectButton,
  NudosTextInput
} from '../../../../../../components/NudosComponents';
import { QRDownloadButton, ToolConfigurationsSection } from '../../../../../../components';
import { IOrganizationConfig } from '../../../../../../types/product.configuration.types';
import { isProductConfigurable } from '../../../../../../utils/configurations';
import SelectReservationSerialCheckbox from '../StockComponents/FloatSelectSerial/Components/SelectReservationSerialCheckbox/SelectReservationSerialCheckbox';
import useStockReservationState from 'state/useStockReservationState';
import MPNInput from './MPNInput/MPNInput';
import { nudosTextcolor } from 'styles/styles.constants';
import BordTooltip from 'components/BordTooltip/BordTooltip';
import { IStockReservation } from 'types/stockOrders';
import useOrdersState from 'state/useOrdersState';
import { getShipmentDestinationText } from '../../../../../../utils/orders';

import './ProductInformationCard.scss';

const ProductInformationCard = ({
  productItem,
  useSameProvider,
  setHasDataChanged,
  useSameMPN,
  isFirstItem,
  currentCommonBuyingPrice,
  countryId,
  countryCode,
  serialErrorsList,
  changeErrorList,
  organizationName,
  updateItemWithinTheList,
  updateWithoutLoaders,
  savedOrganizationConfigs,
  productName,
  organizationId,
  orderId,
  sku,
  productBrand,
  stockReservation,
  orderStatus,
  stockAvailableReservationCount,
  isDelivered
}: {
  productItem: IproductItemData;
  useSameProvider: boolean;
  useSameMPN: boolean;
  setHasDataChanged: React.Dispatch<React.SetStateAction<boolean>>;
  isFirstItem: boolean;
  currentCommonProvider: string;
  currentCommonBuyingPrice: string;
  countryId?: number | null;
  countryCode: string;
  serialErrorsList?: ISerialErrors[];
  changeErrorList?: (items: ISerialErrors[]) => void;
  organizationName?: string;
  updateItemWithinTheList?: (product: IproductItemData) => void;
  updateWithoutLoaders: () => Promise<void>;
  productName: string;
  productBrand?: string;
  savedOrganizationConfigs: IOrganizationConfig[];
  organizationId?: number;
  orderId: string;
  sku: string;
  stockAvailableReservationCount: number;
  stockReservation: IStockReservation | null;
  orderStatus?: string;
  isDelivered?: boolean;
}) => {
  const { stockProductAvailabilityData, setOrderItems } = useStockReservationState();
  const { productsSelected, setProductsSelected } = useOrdersState();
  const [searchString, setSearchString] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [openProviderList, setOpenProviderList] = useState(false);
  const [providersList, setProvidersList] = useState<IproviderInfo[]>();
  const [serialError, setSerialError] = useState<boolean>(false);
  const [serial, setSerial] = useState<string | undefined>('');
  const [purchaseValue, setPurchaseValue] = useState<string | undefined>('');
  const [providerName, setProviderName] = useState<string | undefined>('');

  const isStockReservation = stockReservation !== null;

  const { availableStockInfo } = stockProductAvailabilityData?.find(item => item.sku === sku) || {};
  const { items: availableStockItems } = availableStockInfo || {};

  const currency = productItem?.currency || '0';
  const searchFilter = useDebounce(searchString, 500);
  const isConfigurable = isProductConfigurable(productItem?.category);
  const shipmentDestinationText = getShipmentDestinationText(productItem?.destination);
  const isProductSelected =
    productsSelected?.sku === sku && productsSelected?.items?.some(item => item.saleId === productItem.saleId);
  const purchaseInputValue = (() => {
    if (isStockReservation) return stockReservation?.stockProduct?.value ?? '';

    if (useSameProvider && !isFirstItem) return currentCommonBuyingPrice;

    return productItem?.purchaseValue || '';
  })();

  const configurationsData = {
    configurations: productItem?.configurations || [],
    productName,
    productId: Number(productItem?.productId),
    organizationId,
    orderId,
    nudosCareName: productItem?.nudosCareName,
    category: productItem?.category,
    savedOrganizationConfigs,
    hasConfigurations: !!productItem?.configurations?.length,
    countryId,
    countryCode
  };

  const qrData = {
    serial: productItem?.stockReservation?.stockProduct?.serial ?? productItem?.serial ?? '',
    orgName: organizationName,
    productId: productItem?.productId
  };

  const addStockProductToAvailableStockItems = [
    ...(stockReservation?.stockProduct ? [stockReservation.stockProduct] : []),
    ...(availableStockItems || [])
  ];

  const transformedAvailableStockItems = addStockProductToAvailableStockItems?.map(item => {
    return {
      providerName: item.providerName || '',
      serial: item.serial || '',
      billingNumber: item.invoiceNumber || '',
      date: item.entryDate || '',
      stockProductId: item.stockProductId || 0,
      isFirtsOut: false
    };
  });

  const handleOpenStockOrder = () => {
    window.open(`/stock/details/${stockReservation?.stockOrderUuid}`, '_blank');
  };

  const handleChangeProvider = (newProvider: string, providerId: number) => {
    changeProvider(newProvider, providerId);
    setSearchString(newProvider);
    setOpenProviderList(false);
    setHasDataChanged(true);
  };

  const searchProviders = async () => {
    if (!searchFilter || searchFilter.length === 0) return;
    setLoadingProviders(true);
    try {
      const providers = await getProviders(searchFilter.toLowerCase());
      if (providers) {
        const providersInThisCountry = providers.filter(provider => provider?.countryId === countryId);
        setProvidersList(providersInThisCountry);
        setLoadingProviders(false);
      }
    } catch (error) {
      errorMessage();
      setLoadingProviders(false);
    }
  };

  const createProvider = async () => {
    if (!countryId || !countryCode) return errorMessage();
    const body = { countryId, countryCode, name: searchString };
    setLoadingProviders(true);
    try {
      const response = await createNewProvider(body);
      if (response && response?.name && response?.id) handleChangeProvider(response.name, response.id);
      setOpenProviderList(false);
      setLoadingProviders(false);
    } catch (error) {
      errorMessage();
      setLoadingProviders(false);
    }
  };

  const renderPurchaseValueClassName = ({
    purchaseValue,
    useSameProvider,
    isFirstItem,
    isStockReservation
  }: {
    purchaseValue: string | undefined;
    useSameProvider: boolean;
    isFirstItem: boolean;
    isStockReservation: boolean;
  }): string => {
    const hasPurchaseValue =
      typeof purchaseValue === 'number' || (typeof purchaseValue === 'string' && purchaseValue.length > 0);
    if (!hasPurchaseValue) return '';

    const isDisabled = (useSameProvider && !isFirstItem) || isStockReservation;
    return isDisabled ? 'disabled' : 'active';
  };

  const newProviderOption = (
    <div key={`${productItem.saleId}-newProviderOption`} className="newProviderOption" onClick={createProvider}>
      <div className="searchedText">{searchString} - </div>
      <div className="newText"> Nuevo</div>
    </div>
  );

  const formProvidersOptionsUI = () => {
    if (!providersList || providersList.length === 0) return undefined;
    const providersListJSXElements = providersList?.map((provider, i) => {
      return (
        <div
          key={`provider-${provider.id}-${i}`}
          className="option-container"
          onClick={() => handleChangeProvider(provider.name, provider.id)}
        >
          <p>{provider.name}</p>
        </div>
      );
    });
    const providersListAndNewProviderOption = [...providersListJSXElements, newProviderOption];
    return providersListAndNewProviderOption;
  };

  const defaultSerialError = () => {
    if (!serialErrorsList) return;
    const productSerialError = serialErrorsList.find(
      element => element.serial === serial && element.saleId === productItem.saleId
    );
    if (productSerialError) {
      setSerialError(true);
    } else {
      setSerialError(false);
    }
  };

  const removeSerialError = () => {
    const newList = serialErrorsList?.filter(element => element.saleId !== productItem.saleId);
    if (changeErrorList) changeErrorList(newList || []);
  };

  const changePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!/^[0-9]*[.]?[0-9]*$/.test(value)) return;
    updateItemWithinTheList?.({ ...productItem, purchaseValue: value });
  };

  const changeProvider = (providerName: string, providerId: number) => {
    updateItemWithinTheList?.({ ...productItem, provider: providerName, providerId: providerId });
  };

  const handleCheckSerialButton = () => {
    if (productsSelected?.sku === sku && productsSelected?.items?.some(item => item.saleId === productItem.saleId)) {
      const newItems = productsSelected.items.filter(item => item.saleId !== productItem.saleId);
      if (newItems.length === 0) {
        setProductsSelected(null);
      } else {
        setProductsSelected({
          sku: productsSelected.sku,
          items: newItems
        });
      }
    } else {
      const currentItems = productsSelected?.sku === sku ? productsSelected.items ?? [] : [];
      setProductsSelected({
        sku,
        items: [...currentItems, productItem]
      });
    }
  };

  useEffect(() => {
    if (searchFilter && searchFilter) searchProviders();
  }, [searchFilter]);

  useEffect(() => {
    setSerial(productItem?.serial || '');
  }, [productItem?.serial, productItem]);

  useEffect(() => {
    setPurchaseValue(String(productItem?.purchaseValue || ''));
  }, [productItem?.purchaseValue, productItem]);

  useEffect(() => {
    setProviderName(
      isStockReservation ? stockReservation?.stockProduct?.providerName ?? '' : productItem?.provider ?? ''
    );
    setSearchString(
      isStockReservation ? stockReservation?.stockProduct?.providerName ?? '' : productItem?.provider ?? ''
    );
  }, [productItem?.provider, productItem, stockReservation?.stockProduct?.providerName]);

  useEffect(() => {
    defaultSerialError();
  }, [serialErrorsList]);

  useEffect(() => {
    if (serialError) {
      removeSerialError();
    }
  }, [serial]);

  useEffect(() => {
    if (currentCommonBuyingPrice && useSameProvider) setPurchaseValue(currentCommonBuyingPrice);
  }, [useSameProvider]);

  useEffect(() => {
    setOrderItems([productItem]);
  }, [productItem]);

  return (
    <div className="productInformationCard">
      <div className="productInformationCardHeader">
        <span className="serialContainer">
          <NudosSelectButton
            iconName={isProductSelected ? 'checkmark' : ''}
            isButtonActive={isProductSelected}
            handleClickButton={handleCheckSerialButton}
            componentSize="xs w-3 h-3 checkSingleQrButton"
          />
          <BordOneToneIcon variant="barCode2" stroke={nudosTextcolor} customWidth="12px" />
          <div className="serialTextContainer">
            <span>Serial:</span>
            <span className="serialText">
              {stockReservation?.stockProduct?.serial || productItem?.serial || 'Sin serial'}
            </span>
          </div>
        </span>
        <div className="shipmentDestinationContainer">
          {shipmentDestinationText && (
            <div className="shipmentDestinationText">
              <span>Envío:</span>
              <span className="shipmentDestinationValue">{shipmentDestinationText}</span>
            </div>
          )}
        </div>
      </div>

      <div className="productInformationCardContainer">
        <div className="productInformationCardLeftSection">
          <MPNInput
            handleDataChanged={value => setHasDataChanged(value)}
            useSameMPN={useSameMPN}
            productItem={productItem}
            productBrand={productBrand || ''}
            countryId={countryId}
            countryCode={countryCode}
            updateItemWithinTheList={updateItemWithinTheList}
            isFirstItem={isFirstItem}
          />
          <div
            className={`inputContainer ${renderPurchaseValueClassName({
              purchaseValue,
              useSameProvider,
              isFirstItem,
              isStockReservation
            })}`}
          >
            <div className={`title ${(useSameProvider && !isFirstItem) || isStockReservation ? 'disabled' : ''}`}>
              Costo
            </div>
            <div className="purchaseValue">
              <Input
                className={`inputElement ${(useSameProvider && !isFirstItem) || isStockReservation ? 'disabled' : ''}`}
                placeholder={currency}
                prefix="$"
                disabled={(useSameProvider && !isFirstItem) || isStockReservation}
                onChange={e => {
                  changePrice(e);
                }}
                value={purchaseInputValue}
              />
            </div>
          </div>
          <div
            className={`inputContainer ${
              providerName && providerName.length > 0 && !isStockReservation ? 'active' : ''
            }`}
          >
            <div className={`title ${(useSameProvider && !isFirstItem) || isStockReservation ? 'disabled' : ''}`}>
              Proveedor
            </div>
            <div className={`provider ${useSameProvider && !isFirstItem ? 'sameProvider' : ''}`}>
              <GenericSearchBar
                searchString={searchString}
                setSearchString={setSearchString}
                setIsSelectOpen={setOpenProviderList}
                searchedOptions={formProvidersOptionsUI()}
                isSelectOpen={openProviderList}
                initialValue={providerName ?? undefined}
                handleChangeSelectedOption={(newProvider: string) => handleChangeProvider(newProvider, 0)}
                noResultsMessage={loadingProviders ? <Loading key="loading" /> : newProviderOption}
                isSearchDisabled={(useSameProvider && !isFirstItem) || isStockReservation}
              />
            </div>
          </div>
          <NudosTextInput
            label="N° de orden de proveedor"
            componentSize="small"
            isFilled={
              isStockReservation
                ? !!stockReservation?.stockProduct?.invoiceNumber
                : !!productItem?.providerPurchaseOrder
            }
            placeholder="Agregar N° de la orden"
            defaultValue={
              isStockReservation
                ? stockReservation?.stockProduct?.invoiceNumber ?? ''
                : productItem?.providerPurchaseOrder ?? ''
            }
            handleChange={value => {
              updateItemWithinTheList?.({ ...productItem, providerPurchaseOrder: value });
            }}
            isDeactivated={(useSameProvider && !isFirstItem) || isStockReservation}
          />
        </div>
        <div className="productInformationCardRightSection">
          <div className="qrButtonContainer">
            <QRDownloadButton QRData={qrData} />
          </div>
          <div className="flex items-center gap-2">
            <SelectReservationSerialCheckbox
              isActive={isStockReservation}
              items={transformedAvailableStockItems ?? []}
              onSuccess={updateWithoutLoaders}
              disabled={isDelivered || false}
              orderStatus={orderStatus}
              stockAvailableReservationCount={stockAvailableReservationCount}
              stockReservation={stockReservation}
              currentSingleSaleId={Number(productItem.saleId)}
            />
            {isStockReservation && (
              <BordTooltip position="bottomLeft" message={`Ir a la orden de stock n°${stockReservation?.stockOrderId}`}>
                <div className="isActiveShipmentInfo" onClick={handleOpenStockOrder}>
                  <BordOneToneIcon variant="externalLink" stroke={nudosTextcolor} customWidth="12px" />
                </div>
              </BordTooltip>
            )}
          </div>
        </div>
      </div>
      {isConfigurable && (
        <>
          <div className="grayLineSpace" />
          <ToolConfigurationsSection
            configurationsData={configurationsData}
            updateDataCallback={updateWithoutLoaders}
            showExternalWipingSection={false}
          />
        </>
      )}
    </div>
  );
};

export default ProductInformationCard;
